/**
 * db-viewer.service.ts
 * Core business logic for DB Viewer component
 * Handles query validation, execution, formatting, and state management
 */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TabGetService } from '@techextensor/tab-core-utility';
import { SqlParserService } from './sql-parser.service';
import { format } from 'sql-formatter';
import { Parser } from 'node-sql-parser';
import { GridUpdateState, QueryExecutionResponse, UIQueryParameter, ValidationError } from '../../interfaces/dev-tool/db-viewer.interface';

@Injectable({
  providedIn: 'root'
})
export class DbViewerService {
  
  // SQL Parser
  private sqlParser: Parser;
  
  // SQL Keywords (compiled once)
  private readonly SQL_KEYWORDS = new Set([
    'SELECT', 'FROM', 'WHERE', 'GROUP', 'BY', 'ORDER', 'HAVING', 'LIMIT', 'OFFSET',
    'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER', 'CROSS', 'ON',
    'DISTINCT', 'TOP', 'AS', 'AND', 'OR', 'NOT', 'IN', 'LIKE', 'BETWEEN', 'IS', 'NULL',
    'UNION', 'ALL', 'EXISTS', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'ASC', 'DESC',
    'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE',
    'WITH', 'NOLOCK', 'EXCEPT', 'INTERSECT', 'MINUS', 'WINDOW', 'OVER', 'PARTITION'
  ]);
  
  // Keyword patterns for typo detection
  private readonly KEYWORD_PATTERNS = new Set([
    'inn', 'inne', 'whe', 'wher', 'se', 'sel', 'sele',
    'fr', 'fro', 'gro', 'grou', 'order', 'left', 'right'
  ]);
  
  // Grid state management
  private gridUpdateState = GridUpdateState.IDLE;
  private originalQuery: string = '';
  private parsedOriginalQueryCache: { query: string; parsed: any } | null = null;
  
  // Grid filters/sorts/groups
  private currentGridFilters: any[] = [];
  private currentGridSorts: any[] = [];
  private currentGridGroups: any[] = [];
  
  constructor(
    private sqlParserService: SqlParserService,
    private tabGetService: TabGetService
  ) {
    this.sqlParser = new Parser();
  }
  
  // ==================== VALIDATION ====================
  
  /**
   * Validate SQL query syntax
   */
  validateQuery(query: string): ValidationError[] {
    const errors: ValidationError[] = [];
    
    if (!query.trim()) {
      return errors;
    }
    
    // Remove comments for validation
    const queryWithoutComments = this.removeComments(query);
    if (!queryWithoutComments) {
      return errors;
    }
    
    try {
      this.sqlParser.astify(queryWithoutComments);
    } catch (error: any) {
      const parsedError = this.parseValidationError(error, query, queryWithoutComments);
      if (parsedError) {
        errors.push(parsedError);
      }
    }
    
    return errors;
  }
  
  /**
   * Parse validation error from SQL parser
   */
  private parseValidationError(
    error: any,
    originalQuery: string,
    queryWithoutComments: string
  ): ValidationError | null {
    const errorMessage = error.message || '';
    
    // Check for SQL Server-specific syntax that parser doesn't support
    const hasTopSyntax = /\bselect\s+top\s+\d+/i.test(queryWithoutComments);
    const hasWithSyntax = /\bwith\s+\(/i.test(queryWithoutComments);
    
    if ((hasTopSyntax || hasWithSyntax) && this.isFalsePositiveError(errorMessage)) {
      return null; // Skip false positive
    }
    
    // Extract line and column
    let lineNumber = 1;
    let columnNumber = 1;
    
    if (error.location) {
      lineNumber = error.location.line || error.location.lineNumber || 1;
      columnNumber = error.location.column || error.location.columnNumber || 1;
    } else if (error.line !== undefined) {
      lineNumber = error.line;
      columnNumber = error.column || 1;
    }
    
    // Map to original query line
    const originalLines = originalQuery.split('\n');
    const mappedLineNumber = Math.min(lineNumber, originalLines.length);
    
    // Extract error token
    const currentLineText = originalLines[mappedLineNumber - 1] || '';
    const nearToken = this.extractErrorToken(errorMessage, currentLineText);
    
    if (nearToken) {
      const tokenIndex = currentLineText.toLowerCase().indexOf(nearToken.toLowerCase());
      if (tokenIndex !== -1) {
        columnNumber = tokenIndex + 1;
      }
    }
    
    const message = nearToken 
      ? `Incorrect syntax near '${nearToken}'.`
      : errorMessage;
    
    return {
      message,
      line: mappedLineNumber,
      column: columnNumber,
      severity: 'error'
    };
  }
  
  /**
   * Check if error is a false positive from parser limitations
   */
  private isFalsePositiveError(errorMessage: string): boolean {
    const errorLower = errorMessage.toLowerCase();
    return errorLower.includes('top') || 
           errorLower.includes('but') && /but\s+["']?\d+["']?/i.test(errorMessage);
  }
  
  /**
   * Extract error token from error message
   */
  private extractErrorToken(errorMessage: string, lineText: string): string | null {
    const tokenMatch = errorMessage.match(/but\s+["']?([^"'[\]]+)["']?\s+found/i);
    if (tokenMatch && tokenMatch[1]) {
      return tokenMatch[1].trim();
    }
    return null;
  }
  
  /**
   * Remove SQL comments
   */
  private removeComments(sql: string): string {
    return sql
      .replace(/--.*$/gm, '')
      .replace(/\/\*[\s\S]*?\*\//g, '')
      .trim();
  }
  
  // ==================== FORMATTING ====================
  
  /**
   * Format SQL query using sql-formatter
   */
  formatQuery(sqlQuery: string): { success: boolean; formatted?: string; error?: string } {
    if (!sqlQuery || !sqlQuery.trim()) {
      return { success: false, error: 'No SQL query to format' };
    }
    
    try {
      // Handle SQL Server specific syntax (NOLOCK)
      const nolockPlaceholder = '___NOLOCK___';
      const nolockMatches: string[] = [];
      let nolockIndex = 0;
      
      let queryToFormat = sqlQuery.replace(/\(NOLOCK\)/gi, (match) => {
        nolockMatches.push(match);
        return `(${nolockPlaceholder}${nolockIndex++})`;
      });
      
      // Format with sql-formatter
      let formatted = format(queryToFormat, {
        language: 'sql',
        tabWidth: 4,
        keywordCase: 'upper',
        linesBetweenQueries: 1
      });
      
      // Restore NOLOCK hints
      nolockMatches.forEach((match, index) => {
        formatted = formatted.replace(
          new RegExp(`\\(${nolockPlaceholder}${index}\\)`, 'gi'),
          match
        );
      });
      
      return { success: true, formatted };
    } catch (error: any) {
      return { 
        success: false, 
        error: error.message || 'Failed to format SQL query' 
      };
    }
  }
  
  // ==================== QUERY EXECUTION ====================
  
  /**
   * Execute SQL query
   */
  executeQuery(
    sqlQuery: string,
    parameters: UIQueryParameter[],
    schemaData?: any
  ): Observable<QueryExecutionResponse> {
    return new Observable(observer => {
      // Validate parameters
      if (!this.validateParameters(parameters)) {
        observer.error(new Error('Please fill in all required parameters'));
        observer.complete();
        return;
      }
      
      // Parse SQL to JSON
      let queryJson: any;
      try {
        queryJson = this.sqlParserService.sqlToJson(sqlQuery, schemaData);
        if (!queryJson) {
          observer.error(new Error('Failed to parse SQL query'));
          observer.complete();
          return;
        }
      } catch (parseError: any) {
        observer.error(new Error(`SQL Parse Error: ${parseError.message}`));
        observer.complete();
        return;
      }
      
      // Prepare parameters
      const paramsObj: { [key: string]: any } = {};
      parameters.forEach(param => {
        if (param.value !== null && param.value !== undefined && param.value !== '') {
          paramsObj[param.name] = param.value;
        }
      });
      
      const startTime = Date.now();
      
      // Execute query via TabGetService
      const apiSelectQuery = this.convertToAPISelectQuery(queryJson);
      
      this.tabGetService.selectExecutor(apiSelectQuery, paramsObj).subscribe({
        next: (apiResponse: any) => {
          const response: QueryExecutionResponse = {
            success: apiResponse.IsSuccess,
            data: apiResponse.Result || [],
            metadata: {
              rowCount: apiResponse.TotalRecords || 0,
              executionTime: apiResponse.TotalExecutionTime ? apiResponse.TotalExecutionTime / 1000 : 0,
              hasMore: false
            },
            statusCode: apiResponse.StatusCode,
            recordAffected: apiResponse.RecordAffectted,
            totalRecords: apiResponse.TotalRecords,
            totalExecutionTime: apiResponse.TotalExecutionTime,
            error: apiResponse.Errors && apiResponse.Errors.length > 0
              ? apiResponse.Errors.map((err: any) => typeof err === 'string' ? err : JSON.stringify(err)).join('\n')
              : undefined
          };
          
          observer.next(response);
          observer.complete();
        },
        error: (error) => {
          const errorMessage = error.message || 'Unknown error occurred';
          observer.next({
            success: false,
            data: [],
            metadata: {
              rowCount: 0,
              executionTime: 0,
              hasMore: false
            },
            error: errorMessage
          });
          observer.complete();
        }
      });
    });
  }
  
  /**
   * Validate parameters
   */
  private validateParameters(parameters: UIQueryParameter[]): boolean {
    return parameters.every(p => {
      if (!p.required) return true;
      return p.value !== null && p.value !== undefined && p.value !== '';
    });
  }
  
  /**
   * Convert queryJson to APISelectQuery format
   */
  private convertToAPISelectQuery(queryJson: any): any {
    return queryJson; // Already in correct format from parser
  }
  
  // ==================== PARAMETER DETECTION ====================
  
  /**
   * Detect parameters from SQL query
   */
  detectParameters(sqlQuery: string, existingParameters: UIQueryParameter[]): UIQueryParameter[] {
    const paramRegex = /@(\w+)/g;
    const matches = Array.from(sqlQuery.matchAll(paramRegex));
    const uniqueParams = [...new Set(matches.map(m => m[1]))];
    
    return uniqueParams.map(paramName => {
      const existing = existingParameters.find(p => p.name === paramName);
      return {
        name: paramName,
        value: existing?.value || '',
        type: this.detectParameterType(paramName),
        required: true,
        options: []
      };
    });
  }
  
  /**
   * Detect parameter type from name
   */
  private detectParameterType(paramName: string): 'text' | 'number' | 'date' | 'lookup' {
    const lowerName = paramName.toLowerCase();
    if (lowerName.includes('id')) return 'lookup';
    if (lowerName.includes('date')) return 'date';
    if (lowerName.includes('count') || lowerName.includes('amount') || lowerName.includes('priority')) return 'number';
    return 'text';
  }
  
  // ==================== GRID SYNCHRONIZATION ====================
  
  /**
   * Set grid update state
   */
  setGridUpdateState(state: GridUpdateState): void {
    this.gridUpdateState = state;
  }
  
  /**
   * Get grid update state
   */
  getGridUpdateState(): GridUpdateState {
    return this.gridUpdateState;
  }
  
  /**
   * Check if can update from grid
   */
  canUpdateFromGrid(): boolean {
    return this.gridUpdateState === GridUpdateState.IDLE;
  }
  
  /**
   * Store original query
   */
  setOriginalQuery(query: string): void {
    this.originalQuery = query;
    this.parsedOriginalQueryCache = null; // Invalidate cache
  }
  
  /**
   * Get original query
   */
  getOriginalQuery(): string {
    return this.originalQuery;
  }
  
  /**
   * Update grid filters
   */
  updateGridFilters(filters: any[]): void {
    this.currentGridFilters = filters;
  }
  
  /**
   * Update grid sorts
   */
  updateGridSorts(sorts: any[]): void {
    this.currentGridSorts = sorts;
  }
  
  /**
   * Update grid groups
   */
  updateGridGroups(groups: any[]): void {
    this.currentGridGroups = groups;
  }
  
  /**
   * Check if grid has modifications
   */
  hasGridModifications(): boolean {
    return this.currentGridFilters.length > 0 ||
           this.currentGridSorts.length > 0 ||
           this.currentGridGroups.length > 0;
  }
  
  /**
   * Clear grid modifications
   */
  clearGridModifications(): void {
    this.currentGridFilters = [];
    this.currentGridSorts = [];
    this.currentGridGroups = [];
  }
  
  /**
   * Build SQL from grid modifications
   */
  buildSQLWithGridModifications(baseQuery: string): string | null {
    if (!this.hasGridModifications()) {
      return baseQuery;
    }
    
    try {
      // Parse base query (with caching)
      let parsedQuery: any;
      if (this.parsedOriginalQueryCache && this.parsedOriginalQueryCache.query === baseQuery) {
        parsedQuery = this.parsedOriginalQueryCache.parsed;
      } else {
        parsedQuery = this.sqlParserService.parseQuery(baseQuery);
        this.parsedOriginalQueryCache = { query: baseQuery, parsed: parsedQuery };
      }
      
      // Build new SQL with grid modifications
      return this.buildSQLFromParsedQuery(parsedQuery, baseQuery);
    } catch (error) {
      console.error('Error building SQL from grid modifications:', error);
      return null;
    }
  }
  
  /**
   * Build SQL from parsed query structure
   */
  private buildSQLFromParsedQuery(parsedQuery: any, baseQuery: string): string {
    const parts: string[] = [];
    
    // SELECT clause
    parts.push('SELECT');
    if (parsedQuery.SelectedFields && parsedQuery.SelectedFields.length > 0) {
      parts.push(parsedQuery.SelectedFields.join(', '));
    } else {
      parts.push('*');
    }
    
    // FROM clause (extract from base query to preserve aliases and schema)
    const fromClause = this.extractFromClause(baseQuery);
    if (fromClause) {
      parts.push('FROM ' + fromClause);
    }
    
    // JOIN clauses (preserve from base query)
    const joinClauses = this.extractJoinClauses(baseQuery);
    if (joinClauses) {
      parts.push(joinClauses);
    }
    
    // WHERE clause (combine original + grid filters)
    const whereConditions = this.buildWhereConditions(parsedQuery);
    if (whereConditions.length > 0) {
      parts.push('WHERE ' + whereConditions.join(' AND '));
    }
    
    // GROUP BY clause (grid groups override original)
    const groupByFields = this.currentGridGroups.length > 0
      ? this.currentGridGroups.map(g => g.field)
      : parsedQuery.GroupBy || [];
    
    if (groupByFields.length > 0) {
      parts.push('GROUP BY ' + groupByFields.join(', '));
    }
    
    // ORDER BY clause (grid sorts override original)
    const orderByFields = this.currentGridSorts.length > 0
      ? this.currentGridSorts.map(s => `${s.field} ${s.direction.toUpperCase()}`)
      : (parsedQuery.Sort || []).map((s: any) => `${s.FieldName} ${s.Direction}`);
    
    if (orderByFields.length > 0) {
      parts.push('ORDER BY ' + orderByFields.join(', '));
    }
    
    return parts.join('\n');
  }
  
  /**
   * Build WHERE conditions from parsed query + grid filters
   */
  private buildWhereConditions(parsedQuery: any): string[] {
    const conditions: string[] = [];
    const addedFields = new Set<string>();
    
    // Add original WHERE conditions (skip fields that have grid filters)
    if (parsedQuery.WhereClause?.Filters) {
      parsedQuery.WhereClause.Filters.forEach((filter: any) => {
        const fieldName = filter.FieldName?.toLowerCase().trim();
        if (fieldName) {
          const hasGridFilter = this.currentGridFilters.some(gf =>
            gf.field?.toLowerCase().trim() === fieldName
          );
          
          if (!hasGridFilter) {
            const condition = this.buildFilterCondition(filter);
            if (condition) {
              conditions.push(condition);
              addedFields.add(fieldName);
            }
          }
        }
      });
    }
    
    // Add grid filters
    this.currentGridFilters.forEach(filter => {
      if (filter.field) {
        const condition = this.buildGridFilterCondition(filter);
        if (condition) {
          conditions.push(condition);
        }
      }
    });
    
    return conditions;
  }
  
  /**
   * Build filter condition from parsed filter
   */
  private buildFilterCondition(filter: any): string {
    if (!filter.FieldName) return '';
    
    const fieldName = filter.FieldName;
    const operator = this.getOperatorSymbol(filter.Operator);
    let value = filter.Value;
    
    // Handle NULL checks
    if (filter.Operator === 6) return `${fieldName} IS NULL`;
    if (filter.Operator === 7) return `${fieldName} IS NOT NULL`;
    
    // Handle BETWEEN
    if (filter.Operator === 17 || filter.Operator === 18) {
      if (typeof value === 'string' && value.includes('|')) {
        const [value1, value2] = value.split('|').map(v => v.trim());
        const formattedValue1 = value1.startsWith('@') ? value1 : `'${value1}'`;
        const formattedValue2 = value2.startsWith('@') ? value2 : `'${value2}'`;
        return `${fieldName} ${operator} ${formattedValue1} AND ${formattedValue2}`;
      }
    }
    
    // Handle parameters
    if (filter.ValueType === 2 && value && value.startsWith('@')) {
      return `${fieldName} ${operator} ${value}`;
    }
    
    // Handle string values
    if (typeof value === 'string' && !value.startsWith("'") && !value.startsWith('"') && !value.startsWith('@')) {
      value = `'${value}'`;
    }
    
    return `${fieldName} ${operator} ${value}`;
  }
  
  /**
   * Build grid filter condition
   */
  private buildGridFilterCondition(filter: any): string {
    if (!filter.field || filter.value === null || filter.value === undefined || filter.value === '') {
      return '';
    }
    
    const fieldName = filter.field;
    const operator = this.getOperatorSymbol(parseInt(filter.operator) || 3);
    let value = filter.value;
    
    // Format value
    if (typeof value === 'number') {
      value = String(value);
    } else if (typeof value === 'string') {
      if (value.startsWith('@')) {
        // Parameter - keep as is
      } else if (/^-?\d+(\.\d+)?$/.test(value)) {
        // Numeric string - keep as is
      } else {
        // String value - add quotes
        value = `'${value.replace(/'/g, "''")}'`;
      }
    }
    
    return `${fieldName} ${operator} ${value}`;
  }
  
  /**
   * Get operator symbol from operator number
   */
  private getOperatorSymbol(operatorNumber: number): string {
    const operatorMap: { [key: number]: string } = {
      1: '>', 2: '<', 3: '=', 4: 'IN', 5: 'NOT IN',
      6: 'IS NULL', 7: 'IS NOT NULL', 8: '!=',
      9: '>=', 10: '<=', 11: 'LIKE', 12: 'NOT LIKE',
      17: 'BETWEEN', 18: 'NOT BETWEEN'
    };
    return operatorMap[operatorNumber] || '=';
  }
  
  /**
   * Extract FROM clause from SQL
   */
  private extractFromClause(sql: string): string | null {
    const fromIndex = sql.search(/\bFROM\s+/i);
    if (fromIndex === -1) return null;
    
    const afterFrom = sql.substring(fromIndex + 4).trim();
    
    // Find end of FROM clause
    const stopKeywords = ['WHERE', 'JOIN', 'GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT'];
    let stopPosition = afterFrom.length;
    
    for (const keyword of stopKeywords) {
      const keywordPattern = new RegExp(`\\s+${keyword.replace(/\s+/g, '\\s+')}\\b`, 'i');
      const match = afterFrom.match(keywordPattern);
      if (match && match.index !== undefined && match.index < stopPosition) {
        stopPosition = match.index;
      }
    }
    
    return afterFrom.substring(0, stopPosition).trim();
  }
  
  /**
   * Extract JOIN clauses from SQL
   */
  private extractJoinClauses(sql: string): string | null {
    const joinPattern = /((?:INNER|LEFT|RIGHT|FULL|CROSS)?\s+JOIN\s+\w+(?:\s+(?:AS\s+)?\w+)?\s+ON\s+[\w.]+\s*=\s*[\w.]+)/gi;
    const matches = sql.match(joinPattern);
    return matches ? matches.join('\n') : null;
  }
}